'use client';

import type { MultiValue, OptionProps } from 'react-select';
import type { OptionSportsType } from '@/app/[locale]/projects/model';
import type { User } from '@/types/user/type';
import type { SportType } from '~/model/SportType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import Select, { components } from 'react-select';
import { Loader } from '@/app/components/Loader';
import { useAuthenticatedContext, useSportsContext } from '@/app/components/Provider';
import { useUserSports } from '@/app/hooks/useUserSports';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { buttonValidation } from '~/components/commons/CompleteProfileBanner/utils';
import { MainColumn } from '~/components/commons/MainColumn/MainColumn';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { InfoBar } from '~/components/Journey/InfoBar/InfoBar';
import layout from '~/const/layout';
import { isMainUser } from '~/utils/user/user';
import {
  buildOptions,
  capitalizeString,
  handleOptionSportsFilter,
} from '~/utils/utils';

const { Option } = components;

export const IconOption = (props: OptionProps<{
  label: string;
  id: number;
  value: string;
  data: SportType;
}>) => {
  const isSelected = props.isSelected;
  return (
    <Option {...props}>
      <span
        className={classNames('icon-option', {
          'icon-option--selected': isSelected,
        })}
      >
        {isSelected && <VpIcon name="check" size={16} />}
      </span>
      {' '}
      {props.data.label}
    </Option>
  );
};

export const ProfileCompletionSports = ({
  child,
  lastInformation = false,
  onClose,
}: {
  child?: User;
  lastInformation?: boolean;
  onClose: () => void;
}) => {
  const t = useTranslations();

  const { user } = useAuthenticatedContext();
  const { sports } = useSportsContext();

  const { userSports, isFetching, save } = useUserSports(child);

  const [newSports, setNewSports] = useState<SportType[]>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      const data = newSports ? [...userSports?.['hydra:member'] ?? [], ...newSports] : undefined;
      setIsSubmitting(true);
      await save(data);
      setNewSports(undefined);
      onClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  const handleChange = async (options: MultiValue<OptionSportsType>) => {
    setNewSports(sports['hydra:member'].filter(
      sport => options?.some(option =>
        option.id === sport.id),
    ));
  };

  const placeHolderFavoriteSports = (
    <span className="sports-filter__placeholder">
      <VpIcon name="search" size={20} />
      {child
        ? t('PROFILE.CONTENT.FAVOURITE_SPORTS.CHILDREN.PLACEHOLDER')
        : t('PROFILE.FAVOURITE_SPORTS.SELECT_SPORTS_PLACEHOLDER')}
    </span>
  );

  const sportsOptions = buildOptions(sports?.['hydra:member']);

  const value: OptionSportsType[]
    = [...userSports?.['hydra:member'] ?? [], ...newSports ?? []].map((sport) => {
      return {
        id: sport.id,
        title: capitalizeString(sport.label),
        label: capitalizeString(sport.label),
        value: sport['@id'],
        data: sport,
      };
    }) || [];

  const buttonValidationInfo = buttonValidation(lastInformation);

  return (
    <section
      className="profile-completion__page"
      data-testid="profile-completion-sports"
    >
      <BeardmanTalking
        title={t('PROFILE.COMPLETION.SPORTS.TITLE')}
        description={t('PROFILE.COMPLETION.SPORTS.DESCRIPTION')}
      />
      <MainColumn column={journeyColumnTemplate}>
        <Loader loading={isSubmitting}>
          <label className="vtmn-text-input_label" htmlFor="sports-select">
            {t('PROFILE.COMPLETION.SPORTS.LABEL')}
          </label>
          <Select
            aria-label={t('PROFILE.CONTENT.FAVOURITE_SPORTS.TITLE')}
            className="favourite-sports__list"
            filterOption={handleOptionSportsFilter}
            instanceId="listSports"
            isMulti
            isLoading={isFetching}
            loadingMessage={() => t('SPORTS_FILTER.LOADING')}
            noOptionsMessage={() => t('SPORTS_FILTER.NO_OPTION')}
            onChange={handleChange}
            options={sportsOptions}
            placeholder={placeHolderFavoriteSports}
            styles={layout.profileSportSelectStyles}
            value={value}
          />
          <div className="profile-completion__page__action">
            <VpButton
              data-cy="profile-completion-sports-submit"
              onClick={handleSubmit}
              {...(buttonValidationInfo.icon && {
                endSlot: <VpIcon name={buttonValidationInfo.icon} />,
              })}
              disabled={!!isFetching || isSubmitting}
            >
              {t(buttonValidationInfo.title)}
            </VpButton>
          </div>
          {isMainUser(user) && (
            <InfoBar content={t('PROFILE.COMPLETION.COMMON.DATA_INFO')} />
          )}
        </Loader>
      </MainColumn>
    </section>
  );
};
